import { NgSelectComponent } from '@ng-select/ng-select';
import { variantHasCode } from '../../core/utils/current-ean.util';

/**
 * Scan-to-add boxes (PO / GRN "SKU" and "EAN" ng-selects) are typeaheads with
 * a debounced search. A barcode scanner types the whole code and presses
 * Enter long before that search answers, so ng-select acted on the list left
 * over from the previous scan — Enter re-picked the first product and its
 * details stayed in the entry row.
 *
 * ScanTypeahead remembers which term the current results belong to. An Enter
 * that arrives while they are stale is held back, and once the search for
 * that exact term lands, the result carrying the scanned code is selected
 * through ng-select itself (so the usual (change) handlers run). A partial
 * term typed by hand never auto-selects: only an exact code match does.
 */
export class ScanTypeahead {
  private resultsTerm = '';
  private pendingTerm = '';

  constructor(private readonly select: () => NgSelectComponent | undefined) {}

  /** Bind as the ng-select's [keyDownFn]; false stops it acting on stale rows. */
  readonly keyDown = (e: KeyboardEvent): boolean => {
    if (e.key !== 'Enter') return true;
    const term = (this.select()?.searchTerm ?? '').trim();
    if (!term || term === this.resultsTerm) return true;
    this.pendingTerm = term;
    e.preventDefault();
    return false;
  };

  /** Call for every term the typeahead emits (before debounce). */
  termTyped(term: string): void {
    this.resultsTerm = '';
    if ((term ?? '').trim() !== this.pendingTerm) this.pendingTerm = '';
  }

  /** Call once the results for `term` are stored on the component. */
  resultsArrived(term: string, items: any[]): void {
    this.resultsTerm = (term ?? '').trim();
    if (!this.pendingTerm || this.pendingTerm !== this.resultsTerm) return;
    this.pendingTerm = '';
    const hit = items.find((item) => scanHits(item, this.resultsTerm));
    if (!hit) return;
    // Let ng-select take the new items first.
    setTimeout(() => {
      const sel = this.select();
      const option = sel?.itemsList.filteredItems.find((o) => o.value === hit);
      if (sel && option) sel.select(option);
    });
  }
}

/** Does a product search result carry `code` as its SKU, PLU or a barcode / EAN? */
export function scanHits(item: any, code: string): boolean {
  const c = (code ?? '').trim().toLowerCase();
  if (!c || !item) return false;
  const same = (v: any) => v != null && String(v).trim().toLowerCase() === c;
  return (
    same(item.sku) ||
    same(item.skuCode) ||
    same(item.plu) ||
    same(item.matchedEan) ||
    (item.variants ?? []).some((v: any) => variantHasCode(v, c))
  );
}
